import { css } from '@emotion/react';
import { type FC } from 'react';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import {
  DatePickerComponent,
  DatepickerRole,
} from '~/_shared/baseComponents/datepicker/datepicker.component';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { useTranslation } from '~/_shared/utils/hooks';

const rootStyle = css({
  padding: 10,
  boxSizing: 'border-box',
  width: '100%',
});

const labelStyle = css({
  display: 'block',
  marginBottom: 1,
});

const checkboxWrapperStyle = css({
  marginTop: 10,
});

const checkboxTextStyle = css({
  marginLeft: 8,
  fontSize: 14,
  lineHeight: 1.15,
});

const inputsContainerStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
});

const inputContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
  flexDirection: 'column',
});

export type DateFilterSettings = Readonly<{
  minDate: string;
  maxDate: string;
}>;

export type DateFilterValues = Readonly<{
  currentMaxDate: string | null;
  currentMinDate: string | null;
  includeNotADate: boolean;
  maxDatePlaceholder?: string;
  minDatePlaceholder?: string;
  onChangeIncludeNotADate: (newValue: boolean) => void;
  onChangeMaxDate: (newMax: string) => void;
  onChangeMinDate: (newMin: string) => void;
}>;

type DateFilterProps = DateFilterSettings & DateFilterValues;

export const DateFilterComponent: FC<DateFilterProps> = (props) => {
  const [t] = useTranslation();

  const fromOrToSet = props.currentMinDate !== null || props.currentMaxDate !== null;

  return (
    <div css={rootStyle}>
      <div css={inputsContainerStyle}>
        <div css={inputContainerStyle}>
          <label css={labelStyle}>
            {t('From')}
          </label>
          <DatePickerComponent
            selectedDate={props.currentMinDate}
            onChange={props.onChangeMinDate}
            minDate={props.minDate}
            maxDate={props.currentMaxDate ?? props.maxDate}
            role={DatepickerRole.RangeStartSelector}
            start={props.currentMinDate ?? props.minDate}
            end={props.currentMaxDate ?? props.maxDate}
            placeholderDate={props.minDatePlaceholder}
          />
        </div>
        <div css={inputContainerStyle}>
          <label css={labelStyle}>
            {t('To')}
          </label>
          <DatePickerComponent
            selectedDate={props.currentMaxDate}
            onChange={props.onChangeMaxDate}
            minDate={props.currentMinDate ?? props.minDate}
            maxDate={props.maxDate}
            role={DatepickerRole.RangeEndSelector}
            start={props.currentMinDate ?? props.minDate}
            end={props.currentMaxDate ?? props.maxDate}
            placeholderDate={props.maxDatePlaceholder}
          />
        </div>
      </div>
      <div css={checkboxWrapperStyle}>
        <TooltipComponent
          tooltipContent={fromOrToSet ? undefined : t('filterTool.date.includeNotADate.disabledMessage.fromToNotAvailable')}
          behavior={TooltipBehavior.ShowOnHover}
        >
          <CheckboxComponent
            textStyle={checkboxTextStyle}
            isChecked={props.includeNotADate}
            checkedSetter={() => props.onChangeIncludeNotADate(!props.includeNotADate)}
            text={t('filterTool.date.includeNotADate')}
            isDisabled={!fromOrToSet}
          />
        </TooltipComponent>
      </div>
    </div>
  );
};
